import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import { Grid } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { PageTitleAndSubTitle } from '@HomaHealth/ui-component-library/lib/_AIQ-components';
import Loader from '../../_components/molecules/Loader';
import { PlaybookMessages } from '../../core-utils/messages/PlaybookConstants';
import TabSelection from '../../_components/molecules/TabSelection';
import Dropdown from '@HomaHealth/ui-component-library/lib/_AIQ-components/atoms/Dropdown';
import PlaybookCard from '../../_components/atoms/PlaybookCard';
import { useNavigate } from 'react-router-dom';
import { PLAYBOOK_INTERNAL_PAGE } from '../../core-utils/routes';
import { AlertColor as MuiColor } from '@mui/material/Alert';
import axios from 'axios';
import properties from '../../core-utils/properties';
import { useStores } from '../../stores';
import Snackbar from '../../_components/atoms/Snackbar';

export interface PlaybookPageProps {}

const PlaybookPage: React.FC<PlaybookPageProps> = observer(({}) => {
  const useStyles = makeStyles()({
    root: {
      display: 'flex',
      flexDirection: 'column',
      padding: '40px 0px 66px 63px',
    },
    container: {
      marginBottom: '18px',
      width: '1182px',
      position: 'relative',
    },
  });
  const { classes } = useStyles();
  const messages = PlaybookMessages;
  const navigate = useNavigate();

  const [loader] = useState<any>(<Loader />);
   
  const [loading, setLoading] = useState<boolean>(false);
  const [contractType, setContractType] = useState<string>('');
  const [status, setStatus] = useState<string>('');
  const [acceptedBy, setAcceptedBy] = useState<string>('');
  const [selectedTabIndex, setSelectedTabIndex] = useState<number>(0);
  const [playbookData, setPlaybookData] = useState<any>([]);
  const [snackbarState, setSnackbarState] = React.useState<{
    open: boolean;
    message: string;
    severity: MuiColor | undefined;
    duration: null | number;
  }>({
    open: false,
    message: '',
    severity: undefined,
    duration: null,
  });
  const handleOpenSnackBar = (
    message: string,
    severity: MuiColor,
    duration: number | null,
  ) => {
    setSnackbarState({
      open: true,
      message: message,
      severity: severity,
      duration: duration,
    });
  };
  const handleCloseSnackBar = (
    event?: React.SyntheticEvent,
    reason?: string,
  ) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarState({
      open: false,
      message: '',
      severity: undefined,
      duration: null,
    });
  };

  const employerGroupStore = useStores().employerGroupStore;
  const contractAiqStore = useStores().contractAiqStore;

  const handleSelectedIndex = (index: number) => {
    setSelectedTabIndex(index);
  };

  const getPlaybookData = async () => {
    setLoading(true);
    await axios
      .post(
        properties.aiqBaseUrl +
          properties.playbookBaseUrl +
          properties.masterPlaybooks,
        {
          eg_nid: employerGroupStore?.employerGroupResponseData?.eg_nid,
          typeOfPlaybook: selectedTabIndex === 0 && 'purchasing_standards',
        },
      )
      .then((response) => {
        if (response.status === 200) {
          setPlaybookData(response.data?.data);
        }
      })
      .catch((error) =>
        handleOpenSnackBar(error.response?.data?.error, 'error', 6000),
      )
      .finally(() => {
        setLoading(false);
      });
  };

  const acceptPlaybook = async (
    eg_nid: String,
    playbook_id: String,
    type_of_report: string,
  ) => {
    await axios
      .post(
        properties.aiqBaseUrl +
          properties.playbookBaseUrl +
          properties.acceptPlaybook,
        {
          eg_nid: eg_nid,
          playbook_id: playbook_id,
          type_of_report: type_of_report,
        },
      )
      .then((response) => {
        if (response.status === 200) {
          getPlaybookData();
        }
      })
      .catch((error) =>
        handleOpenSnackBar(error.response?.data?.error, 'error', 6000),
      );
  };

  useEffect(() => {
    getPlaybookData();
  }, [selectedTabIndex]);

  const PurchasingStandard = () => {
    const useStyles = makeStyles()({
      root: {
        display: 'flex',
        flexDirection: 'column',
        width: '1182px',
      },
      dropDownContainer: {
        display: 'flex',
        flexDirection: 'row',
        gap: '0.5rem',
        justifyContent: 'flex-end',
        marginBottom: '18px',
      },
      cardContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: '1rem',
      },
    });
    const { classes } = useStyles();
    return (
      <Grid container className={classes.root}>
        <Grid className={classes.dropDownContainer}>
          <Dropdown
            label={messages.FILTER_DROPDOWN.CONTRACT_TYPE.TITLE}
            value={contractType}
            setValue={setContractType}
            dropDownOptions={
              messages.FILTER_DROPDOWN.CONTRACT_TYPE.DROPDOWNOPTIONS
            }
            dropDownValues={
              messages.FILTER_DROPDOWN.CONTRACT_TYPE.DROPDOWNVALUES
            }
            selectStyles={{
              height: '36px',
              width: '150px',
            }}
          />
          <Dropdown
            label={messages.FILTER_DROPDOWN.STATUS.TITLE}
            value={status}
            setValue={setStatus}
            dropDownOptions={messages.FILTER_DROPDOWN.STATUS.DROPDOWNOPTIONS}
            dropDownValues={messages.FILTER_DROPDOWN.STATUS.DROPDOWNVALUES}
            selectStyles={{
              height: '36px',
              width: '150px',
            }}
          />
          <Dropdown
            label={messages.FILTER_DROPDOWN.ACCEPTED_BY.TITLE}
            value={acceptedBy}
            setValue={setAcceptedBy}
            dropDownOptions={
              messages.FILTER_DROPDOWN.ACCEPTED_BY.DROPDOWNOPTIONS
            }
            dropDownValues={messages.FILTER_DROPDOWN.ACCEPTED_BY.DROPDOWNVALUES}
            selectStyles={{
              height: '36px',
              width: '150px',
            }}
          />
        </Grid>
        <Grid className={classes.cardContainer}>
          {playbookData.map((card: any) => (
            <PlaybookCard
              title={card.playbook_name}
              subTitle={card.summary}
              isAccepted={card.flag}
              applicableTo={card.service_agreement}
              acceptedBy={card.user_name}
              redirectAction={() => {
                contractAiqStore.setPlaybookData(
                  card.eg_nid,
                  card.playbook_id,
                  card.type_of_playbook,
                );
                navigate(PLAYBOOK_INTERNAL_PAGE);
              }}
              buttonAction={() => {
                if (card.flag) {
                  contractAiqStore.setPlaybookData(
                    card.eg_nid,
                    card.playbook_id,
                    card.type_of_playbook,
                  );
                  navigate(PLAYBOOK_INTERNAL_PAGE);
                } else {
                  acceptPlaybook(
                    card.eg_nid,
                    card.playbook_id,
                    card.type_of_playbook,
                  );
                }
              }}
            />
          ))}
        </Grid>
      </Grid>
    );
  };

  const RegulatoryCompliance = () => {
    const useStyles = makeStyles()({
      root: {
        display: 'flex',
        flexDirection: 'column',
        width: '1182px',
      },
      dropDownContainer: {
        display: 'flex',
        flexDirection: 'row',
        gap: '0.5rem',
        justifyContent: 'flex-end',
        marginBottom: '18px',
      },
      cardContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: '1rem',
      },
    });
    const { classes } = useStyles();
    return (
      <Grid container className={classes.root}>
        <Grid className={classes.dropDownContainer}>
          <Dropdown
            label={messages.FILTER_DROPDOWN.CONTRACT_TYPE.TITLE}
            value={contractType}
            setValue={setContractType}
            dropDownOptions={
              messages.FILTER_DROPDOWN.CONTRACT_TYPE.DROPDOWNOPTIONS
            }
            dropDownValues={
              messages.FILTER_DROPDOWN.CONTRACT_TYPE.DROPDOWNVALUES
            }
            selectStyles={{
              height: '36px',
              width: '150px',
            }}
          />
        </Grid>
        <Grid className={classes.cardContainer}>
          {playbookData.map((card: any) => (
            <PlaybookCard
              title={card.playbook_name}
              subTitle={card.summary}
              isAccepted={card.flag}
              applicableTo={card.service_agreement}
              acceptedBy={card.user_name}
              isDefaultAccepted={true}
              buttonAction={() => {
                contractAiqStore.setPlaybookData(
                  card.eg_nid,
                  card.playbook_id,
                  card.type_of_playbook,
                );
                navigate(PLAYBOOK_INTERNAL_PAGE);
              }}
            />
          ))}
        </Grid>
      </Grid>
    );
  };

  return (
    <>
      {loading ? (
        loader
      ) : (
        <Grid container className={classes.root}>
          <Grid className={classes.container}>
            <PageTitleAndSubTitle
              title={messages.MAIN.TITLE}
              subTitle={messages.MAIN.SUBTITLE}
            />

            <TabSelection
              selectedTabIndex={selectedTabIndex}
              setSelectedTabIndex={handleSelectedIndex}
              tabList={messages.tabs}
            />
          </Grid>
          <Grid>
            {selectedTabIndex === 0 && <PurchasingStandard />}
            {selectedTabIndex === 1 && <RegulatoryCompliance />}
          </Grid>
          <Snackbar
            {...snackbarState}
            handleClose={handleCloseSnackBar}
            vertical="bottom"
            horizontal="right"
          />
        </Grid>
      )}
    </>
  );
});

export default PlaybookPage;
